import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Boton } from "../components/Boton";
import { Campo, Input } from "../components/Formulario";
import { Tarjeta } from "../components/Tarjeta";
import { api, ErrorApi } from "../lib/apiClient";
import { useAuth } from "./AuthContext";

// Sirve tanto /login (campo Estudio editable) como /acceso/:slug (el slug
// viene de la URL, precargado y bloqueado) — un solo componente, un solo
// flujo de autenticación; la diferencia es puramente de UX.
export function LoginPage() {
  const { slug: slugDeUrl } = useParams<{ slug?: string }>();
  const { usuario, cargandoInicial, iniciarSesion } = useAuth();
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const [slug, setSlug] = useState(slugDeUrl ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [tieneLogo, setTieneLogo] = useState(false);

  // Endpoint público (sin sesión, ver routes/publico.ts) — así el login
  // puede mostrar el logo del estudio antes de autenticarse. Con
  // debounce en /login (el slug se tipea); inmediato en /acceso/:slug
  // (ya viene resuelto en la URL).
  useEffect(() => {
    if (!slug) {
      setTieneLogo(false);
      return;
    }
    const temporizador = setTimeout(() => {
      api
        .get<{ nombre: string; tieneLogo: boolean }>(`/publico/estudio/${slug}`)
        .then((d) => setTieneLogo(d.tieneLogo))
        .catch(() => setTieneLogo(false));
    }, slugDeUrl ? 0 : 400);
    return () => clearTimeout(temporizador);
  }, [slug, slugDeUrl]);

  if (!cargandoInicial && usuario) {
    const destino = (ubicacion.state as { desde?: string } | null)?.desde ?? "/dashboard";
    return <Navigate to={destino} replace />;
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await iniciarSesion(slug, email, password);
      navegar("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : "No se pudo iniciar sesión. Intentá nuevamente.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-fondo px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          {tieneLogo ? (
            <img
              src={`/api/publico/estudio/${slug}/logo`}
              alt="Logo del estudio"
              className="mx-auto mb-3 h-10 w-10 rounded-lg object-contain"
              onError={() => setTieneLogo(false)}
            />
          ) : (
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-principal text-lg font-semibold text-superficie">
              J
            </div>
          )}
          <h1 className="text-lg font-semibold text-texto">ERP Jurídico JVD</h1>
          <p className="mt-1 text-sm text-texto-secundario">Ingresá con tu cuenta del estudio</p>
        </div>
        <Tarjeta tamano="grande">
          <form onSubmit={manejarEnvio}>
            <div className="space-y-4">
              <Campo etiqueta="Estudio" obligatorio>
                <Input
                  type="text"
                  required
                  disabled={Boolean(slugDeUrl)}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="identificador-del-estudio"
                />
              </Campo>
              <Campo etiqueta="Email" obligatorio>
                <Input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@estudio.com"
                />
              </Campo>
              <Campo etiqueta="Contraseña" obligatorio>
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </Campo>
            </div>
            {error && <div className="mt-4 rounded-lg bg-error/10 px-3 py-2 text-sm font-medium text-error">{error}</div>}
            <Boton type="submit" cargando={enviando} className="mt-5 w-full">
              Ingresar
            </Boton>
          </form>
        </Tarjeta>
        <p className="mt-6 text-center text-xs text-texto-secundario">
          Demo: estudio "estudio-demo" · admin@estudio-demo.test · abogada@estudio-demo.test — contraseña ClaveDemo123!
        </p>
      </div>
    </div>
  );
}
